import { supabase } from './supabase'
import type { Profile, Task, TaskStatus } from '../types'

export type DashboardView = 'ALL_VISIBLE' | 'ASSIGNED_BY_ME' | 'ASSIGNED_TO_ME' | 'ALL_OPEN' | 'COMPLETED_RECENT'
export type AdminScope = 'OVERALL' | 'PERSON' | 'TEAM'

export interface TaskRegisterRow extends Task {
  blocked_reason?: string | null
  blocked_since?: string | null
  delay_count?: number
  revision_dates?: string[]
  assigned_on_behalf_of_id?: string | null
  repeat_type?: 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY'
}

export interface NotificationRow {
  id: string
  recipient_id: string
  task_id: string | null
  kind: string
  title: string
  body: string
  channel: string
  status: string
  scheduled_for: string
  sent_at: string | null
  read_at: string | null
  metadata: Record<string, unknown>
  created_at: string
}

export interface ActivityRow {
  id: string
  task_id: string | null
  event_type: string
  actor_id: string | null
  event_time: string
  metadata: Record<string, unknown>
  notes: string | null
  actor?: Pick<Profile, 'id' | 'full_name'> | null
  task?: Pick<Task, 'id' | 'task_code' | 'title'> | null
}

export interface HelpTicketRow {
  id: string
  task_id: string
  requested_by_id: string
  helper_id: string
  suggestion_1: string | null
  suggestion_2: string | null
  suggestion_3: string | null
  help_note: string | null
  status: string
  created_at: string
  resolved_at: string | null
  sla_due_at: string
  task?: Pick<Task, 'id' | 'task_code' | 'title'> | null
  requester?: Pick<Profile, 'id' | 'full_name'> | null
  helper?: Pick<Profile, 'id' | 'full_name'> | null
}

export interface WeeklyReviewOwnerRow {
  owner_id: string
  owner_name: string
  ytd_green: number
  ytd_yellow: number
  ytd_red: number
  last_target_green: number
  last_target_yellow: number
  last_target_red: number
  last_actual_green: number
  last_actual_yellow: number
  last_actual_red: number
  this_target_green: number
  this_target_yellow: number
  this_target_red: number
  status: 'ACHIEVED' | 'NOT_ACHIEVED' | 'NO_DATA'
}

export interface LeaderboardRow {
  user_id: string
  full_name: string
  green_count: number
  yellow_count: number
  red_count: number
  unscored_count: number
  earned_points: number
  max_points: number
  percentage: number
}

export interface DueDateMissRow {
  task_id: string
  task_code: string
  title: string
  priority: string
  status: string
  employee_id: string
  employee_name: string
  employee_team: string | null
  assigned_at: string
  due_date_deadline: string
  missed_at: string
  current_due_date: string | null
  resolution: 'STILL_NOT_APPLIED' | 'APPLIED_LATE'
}

export interface WeeklyTargetInput {
  weekStart: string
  ownerId?: string | null
  stage?: string
  carryForwardCount?: number
  greenPercent: number
  yellowPercent: number
  redPercent: number
}

export interface TaskEditInput {
  taskId: string
  title: string
  description: string
  assignedToId: string
  assignedOnBehalfOfId?: string | null
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
  repeatType: 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY'
  comment?: string | null
}

export async function getDashboardMetricsV2(args: {
  from?: string | null; to?: string | null; view?: DashboardView; scope?: AdminScope; personId?: string | null; team?: string | null
}) {
  const { data, error } = await supabase.rpc('get_dashboard_metrics_v2', {
    p_from: args.from ?? null, p_to: args.to ?? null, p_view: args.view ?? 'ALL_VISIBLE',
    p_scope: args.scope ?? 'OVERALL', p_person_id: args.personId ?? null, p_team: args.team ?? null,
  })
  if (error) throw error
  return data
}

export async function getLeaderboardV2(args: {
  from?: string | null; to?: string | null; scope?: AdminScope; personId?: string | null; team?: string | null
}): Promise<LeaderboardRow[]> {
  const { data, error } = await supabase.rpc('get_leaderboard_v2', {
    p_from: args.from ?? null,
    p_to: args.to ?? null,
    p_scope: args.scope ?? 'OVERALL',
    p_person_id: args.personId ?? null,
    p_team: args.team ?? null,
  })
  if (error) throw error
  return (data ?? []).map((row: any) => ({
    ...row,
    green_count: Number(row.green_count ?? 0),
    yellow_count: Number(row.yellow_count ?? 0),
    red_count: Number(row.red_count ?? 0),
    unscored_count: Number(row.unscored_count ?? 0),
    earned_points: Number(row.earned_points ?? 0),
    max_points: Number(row.max_points ?? 0),
    percentage: Number(row.percentage ?? 0),
  })) as LeaderboardRow[]
}

export async function getTaskRegister(args: {
  from?: string | null; to?: string | null; view?: DashboardView; scope?: AdminScope; personId?: string | null; team?: string | null;
  status?: string | null; priority?: string | null; search?: string | null; page?: number; pageSize?: number
}): Promise<{ rows: TaskRegisterRow[]; total: number; page: number; page_size: number }> {
  const { data, error } = await supabase.rpc('get_task_register', {
    p_from: args.from ?? null, p_to: args.to ?? null, p_view: args.view ?? 'ALL_VISIBLE', p_scope: args.scope ?? 'OVERALL',
    p_person_id: args.personId ?? null, p_team: args.team ?? null, p_status: args.status ?? null, p_priority: args.priority ?? null,
    p_search: args.search ?? null, p_page: args.page ?? 1, p_page_size: args.pageSize ?? 50,
  })
  if (error) throw error
  return { rows: (data?.rows ?? []) as TaskRegisterRow[], total: Number(data?.total ?? 0), page: Number(data?.page ?? 1), page_size: Number(data?.page_size ?? 50) }
}


export async function getTaskExport(args: {
  from?: string | null; to?: string | null; view?: DashboardView; scope?: AdminScope; personId?: string | null; team?: string | null;
  status?: string | null; priority?: string | null; search?: string | null
}) {
  const { data, error } = await supabase.rpc('get_task_export', {
    p_from: args.from ?? null, p_to: args.to ?? null, p_view: args.view ?? 'ALL_VISIBLE', p_scope: args.scope ?? 'OVERALL',
    p_person_id: args.personId ?? null, p_team: args.team ?? null, p_status: args.status ?? null, p_priority: args.priority ?? null, p_search: args.search ?? null,
  })
  if (error) throw error
  return (data ?? []) as any[]
}

export async function getAnalyticsMetrics(args: { from?: string | null; to?: string | null; scope?: AdminScope; personId?: string | null; team?: string | null }) {
  const { data, error } = await supabase.rpc('get_analytics_metrics', {
    p_from: args.from ?? null, p_to: args.to ?? null, p_scope: args.scope ?? 'OVERALL', p_person_id: args.personId ?? null, p_team: args.team ?? null,
  })
  if (error) throw error
  return data
}

export async function getWeeklyReview(args: { weekStart?: string | null; scope?: AdminScope; personId?: string | null; team?: string | null }) {
  const { data, error } = await supabase.rpc('get_weekly_review', {
    p_week_start: args.weekStart ?? null, p_scope: args.scope ?? 'OVERALL', p_person_id: args.personId ?? null, p_team: args.team ?? null,
  })
  if (error) throw error
  return data
}

export async function getWeeklyReviewOwners(args: { weekStart?: string | null; scope?: AdminScope; personId?: string | null; team?: string | null }) {
  const { data, error } = await supabase.rpc('get_weekly_review_owners', {
    p_week_start: args.weekStart ?? null,
    p_scope: args.scope ?? 'OVERALL',
    p_person_id: args.personId ?? null,
    p_team: args.team ?? null,
  })
  if (error) throw error
  return (data ?? []) as WeeklyReviewOwnerRow[]
}

export async function getWeeklyTargets(args: { weekStart?: string | null; scope?: AdminScope; personId?: string | null; team?: string | null } = {}) {
  const { data, error } = await supabase.rpc('get_weekly_targets_filtered', {
    p_week_start: args.weekStart ?? null, p_scope: args.scope ?? 'OVERALL', p_person_id: args.personId ?? null, p_team: args.team ?? null,
  })
  if (error) throw error
  return (data ?? []) as any[]
}

export async function setWeeklyTarget(input: WeeklyTargetInput) {
  const { data, error } = await supabase.rpc('set_weekly_target', {
    p_week_start: input.weekStart,
    p_owner_id: input.ownerId ?? null,
    p_stage: input.stage ?? 'TaskOps Review',
    p_carry_forward_count: input.carryForwardCount ?? 0,
    p_green_percent: input.greenPercent,
    p_yellow_percent: input.yellowPercent,
    p_red_percent: input.redPercent,
  })
  if (error) throw error
  return data
}

export async function updateTaskDetails(input: TaskEditInput) {
  const { data, error } = await supabase.rpc('update_task_details', {
    p_task_id: input.taskId,
    p_title: input.title,
    p_description: input.description,
    p_assigned_to_id: input.assignedToId,
    p_assigned_on_behalf_of_id: input.assignedOnBehalfOfId ?? null,
    p_priority: input.priority,
    p_repeat_type: input.repeatType,
    p_comment: input.comment ?? null,
  })
  if (error) throw error
  return data
}

export async function getNotifications(limit = 30): Promise<NotificationRow[]> {
  const { data, error } = await supabase.from('notifications')
    .select('id,recipient_id,task_id,kind,title,body,channel,status,scheduled_for,sent_at,read_at,metadata,created_at')
    .eq('channel', 'IN_APP')
    .order('created_at', { ascending: false }).limit(limit)
  if (error) throw error
  return (data ?? []) as NotificationRow[]
}

export async function markNotificationRead(id: string) {
  const { error } = await supabase.from('notifications').update({ read_at: new Date().toISOString(), status: 'READ' }).eq('id', id)
  if (error) throw error
}

export async function markAllNotificationsRead() {
  const { error } = await supabase.from('notifications').update({ read_at: new Date().toISOString(), status: 'READ' }).is('read_at', null)
  if (error) throw error
}

export async function updateTaskDueDate(taskId: string, dueDate: string) {
  const { error } = await supabase.from('tasks').update({ current_due_date: dueDate }).eq('id', taskId)
  if (error) throw error
}

export async function blockTask(taskId: string, reason: string) {
  const { error } = await supabase.from('tasks').update({ status: 'BLOCKED' satisfies TaskStatus, blocked_reason: reason.trim(), blocked_since: new Date().toISOString() }).eq('id', taskId)
  if (error) throw error
}

export async function submitTaskForApproval(taskId: string) {
  const { error } = await supabase.from('tasks').update({ status: 'PENDING_APPROVAL' satisfies TaskStatus, submitted_at: new Date().toISOString() }).eq('id', taskId)
  if (error) throw error
  try {
    const { data, error: emailError } = await supabase.functions.invoke('task-workflow-email', { body: { task_id: taskId } })
    if (emailError || data?.success === false) console.warn('Task submitted, but approval email could not be sent:', emailError?.message || data?.error)
  } catch (emailError) {
    console.warn('Task submitted, but approval email could not be sent:', emailError)
  }
}

export async function approveTask(taskId: string, actorId: string) {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })
  const { error } = await supabase.from('tasks').update({ status: 'COMPLETED' satisfies TaskStatus, completion_date: today, approved_at: new Date().toISOString(), approved_by_id: actorId, approval_rejection_reason: null }).eq('id', taskId)
  if (error) throw error
}

export async function reopenTask(taskId: string, reason: string) {
  const { error } = await supabase.from('tasks').update({ status: 'REOPENED' satisfies TaskStatus, approval_rejection_reason: reason.trim() || 'Reopened for correction' }).eq('id', taskId)
  if (error) throw error
}

export async function cancelTask(taskId: string) {
  const { error } = await supabase.from('tasks').update({ status: 'CANCELLED' satisfies TaskStatus }).eq('id', taskId)
  if (error) throw error
}

export async function createHelpTicket(input: {
  task: TaskRegisterRow
  requesterId: string
  helperId: string
  suggestion1?: string
  suggestion2?: string
  suggestion3?: string
  note?: string
}) {
  const { task, requesterId, helperId, suggestion1, suggestion2, suggestion3, note } = input
  if (!helperId) throw new Error('Select a helper.')
  const { error } = await supabase.from('help_tickets').insert({
    task_id: task.id,
    requested_by_id: requesterId,
    helper_id: helperId,
    suggestion_1: suggestion1?.trim() || null,
    suggestion_2: suggestion2?.trim() || null,
    suggestion_3: suggestion3?.trim() || null,
    help_note: note?.trim() || 'Help requested from TaskOps',
    status: 'OPEN',
    sla_due_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  })
  if (error) throw error
}

export async function getHelpTickets(): Promise<HelpTicketRow[]> {
  const { data, error } = await supabase.from('help_tickets')
    .select('id,task_id,requested_by_id,helper_id,suggestion_1,suggestion_2,suggestion_3,help_note,status,created_at,resolved_at,sla_due_at,task:tasks!help_tickets_task_id_fkey(id,task_code,title),requester:profiles!help_tickets_requested_by_id_fkey(id,full_name),helper:profiles!help_tickets_helper_id_fkey(id,full_name)')
    .order('created_at', { ascending: false }).limit(200)
  if (error) throw error
  return (data ?? []) as unknown as HelpTicketRow[]
}

export async function resolveHelpTicket(id: string, suggestion: string) {
  const { error } = await supabase.from('help_tickets').update({ status: 'RESOLVED', suggestion_1: suggestion.trim() || 'Resolved', resolved_at: new Date().toISOString() }).eq('id', id)
  if (error) throw error
}

export async function getActivities(limit = 200): Promise<ActivityRow[]> {
  const { data, error } = await supabase.rpc('get_activity_feed', {
    p_limit: limit,
  })
  if (error) throw error
  return (data ?? []) as unknown as ActivityRow[]
}

export async function getDueDateMisses(): Promise<DueDateMissRow[]> {
  const { data, error } = await supabase.rpc('get_admin_due_date_misses')
  if (error) throw error
  return (data ?? []) as DueDateMissRow[]
}

export async function getTaskActivity(taskId: string): Promise<ActivityRow[]> {
  const { data, error } = await supabase.from('task_activity')
    .select('id,task_id,event_type,actor_id,event_time,metadata,notes,actor:profiles!task_activity_actor_id_fkey(id,full_name),task:tasks!task_activity_task_id_fkey(id,task_code,title)')
    .eq('task_id', taskId).order('event_time', { ascending: false }).limit(100)
  if (error) throw error
  return (data ?? []) as unknown as ActivityRow[]
}

